import { useState } from 'react';
import { ExportOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Drawer,
  Empty,
  Flex,
  Space,
  Switch,
  Skeleton,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import { Numero, GraficoDeUso } from './UsoDoArquivo';
import type { Exam, ExamFileKind, UsoDaProva } from '@titularis/api-client';
import { examFileLabel } from '@titularis/ui';
import { api } from '../api';
import { enderecoDaProva } from '../site';

/**
 * A distribuição pública de uma prova, no cadastro dela.
 *
 * Duas coisas, e elas se explicam uma à outra: a **chave** que põe os arquivos
 * em Provas para Imprimir, e a **medida** do que aconteceu depois. Separá-las
 * em duas telas faria a pergunta "vale a pena liberar?" ficar a dois cliques da
 * resposta.
 *
 * A chave é vizinha do `status` da prova, e não a mesma: publicar põe as
 * **questões** no app, liberar põe os **arquivos** na web aberta - o PDF que
 * sobe para o Cadastro Assistido é insumo de trabalho, e não peça publicada.
 */
export function DistribuicaoDaProva({ prova }: { prova: Exam }) {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [vendoQuem, setVendoQuem] = useState(false);

  const temArquivo = Boolean(prova.examFilePath ?? prova.answerKeyFilePath ?? prova.editalFilePath);
  const endereco = prova.slug ? enderecoDaProva(prova.slug) : null;

  const liberar = useMutation({
    mutationFn: (filesPublic: boolean) => api.setExamFilesPublic(prova.id, filesPublic),
    onSuccess: async (atualizada) => {
      message.success(
        atualizada.filesPublic
          ? 'Arquivos liberados. A prova já aparece em Provas para Imprimir.'
          : 'Arquivos recolhidos. A página pública da prova saiu do ar.',
      );
      await queryClient.invalidateQueries({ queryKey: ['exam', prova.id] });
      await queryClient.invalidateQueries({ queryKey: ['exams'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /*
   * Os números só são pedidos depois de a prova ter sido liberada alguma vez.
   * Antes disso não há o que medir, e a consulta seria três `count` sobre nada.
   */
  const engajamento = useQuery({
    queryKey: ['exam-engagement', prova.id],
    queryFn: () => api.examEngagement(prova.id),
    enabled: prova.filesPublic,
  });

  const numeros = engajamento.data;
  const houveUso = Boolean(numeros && numeros.views + numeros.prints + numeros.downloads > 0);

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Flex align="flex-start" gap={12} justify="space-between">
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>Distribuir em Provas para Imprimir</Typography.Text>
          <Typography.Text type="secondary">
            A página abre sem login; baixar pede conta gratuita.
          </Typography.Text>
        </Space>
        <Switch
          checked={prova.filesPublic}
          loading={liberar.isPending}
          disabled={!temArquivo && !prova.filesPublic}
          onChange={(marcado) => liberar.mutate(marcado)}
          aria-label="Distribuir os arquivos desta prova"
        />
      </Flex>

      {!temArquivo ? (
        <Alert
          type="info"
          showIcon
          message="Envie ao menos um arquivo - prova, gabarito ou edital - para poder liberar."
        />
      ) : null}

      {prova.filesPublic && endereco ? (
        <Space orientation="vertical" size={4} style={{ width: '100%' }}>
          <Typography.Text type="secondary">
            O link para mandar a quem pediu a prova:
          </Typography.Text>
          {/*
            Copiável, porque é para isso que ele existe: o módulo inteiro nasceu
            de "sempre que pedirem, mandamos o link da Titularis".
          */}
          <Typography.Paragraph copyable={{ text: endereco }} style={{ marginBottom: 0 }}>
            <a href={endereco} target="_blank" rel="noopener noreferrer">
              {endereco} <ExportOutlined />
            </a>
          </Typography.Paragraph>
        </Space>
      ) : null}

      {prova.filesPublic ? (
        <>
          {/*
            **Enquanto a resposta não chega, não se mostra zero.** Escrever
            "0 downloads" enquanto se carrega é dizer uma coisa errada em voz
            alta - e foi o que apareceu na validação, numa prova que já tinha
            três. Melhor a esqueleto do que um número que muda sozinho.
          */}
          {engajamento.isPending ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          ) : (
            <Flex gap={24} wrap>
              <Numero
                rotulo="Downloads"
                valor={numeros?.downloads ?? 0}
                destaque
              />
              <Numero rotulo="Impressões pedidas" valor={numeros?.prints ?? 0} />
              <Numero rotulo="Aberturas" valor={numeros?.views ?? 0} />
            </Flex>
          )}

          {/*
            "Pedidas", e não "impressões": o que se conta é o clique no nosso
            botão, e o diálogo do navegador ainda pode ser cancelado. O rótulo
            diz o que o número é - número que finge precisão que não tem é pior
            que número nenhum.
          */}
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Impressão pedida é o clique no botão Imprimir: o diálogo do navegador pode ter sido
            cancelado depois.
          </Typography.Text>

          {houveUso && !engajamento.isPending ? (
            <>
              <GraficoDeUso serie={numeros?.serie ?? []} />
              <Button onClick={() => setVendoQuem(true)}>Ver quem baixou</Button>
            </>
          ) : engajamento.isPending ? null : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Ninguém abriu esta prova ainda."
            />
          )}
        </>
      ) : null}

      <Drawer
        title="Quem baixou e quem mandou imprimir"
        // `min` porque o backoffice também é aberto no celular: 640px fixos
        // sairiam da tela num aparelho de 411px.
        size="min(640px, 100vw)"
        open={vendoQuem}
        onClose={() => setVendoQuem(false)}
        destroyOnHidden
      >
        <QuemUsou examId={prova.id} />
      </Drawer>
    </Space>
  );
}

/** A lista de quem levou a prova embora. Não sai do backoffice. */
function QuemUsou({ examId }: { examId: string }) {
  const [pagina, setPagina] = useState(1);

  const usos = useQuery({
    queryKey: ['exam-engagement-people', examId, pagina],
    queryFn: () => api.examEngagementPeople(examId, { page: pagina, pageSize: 20 }),
  });

  return (
    <Table<UsoDaProva>
      size="small"
      rowKey={(linha) => `${linha.createdAt}-${linha.userId ?? 'anonimo'}-${linha.action}`}
      loading={usos.isPending}
      dataSource={usos.data?.items ?? []}
      pagination={{
        current: pagina,
        pageSize: 20,
        total: usos.data?.total ?? 0,
        onChange: setPagina,
        showSizeChanger: false,
      }}
      columns={[
        {
          title: 'Quem',
          dataIndex: 'name',
          render: (_: unknown, linha) =>
            linha.userId ? (
              <Space orientation="vertical" size={0}>
                <Typography.Text>{linha.name ?? 'Conta excluída'}</Typography.Text>
                <Typography.Text type="secondary">{linha.email}</Typography.Text>
              </Space>
            ) : (
              // Só a impressão chega anônima: baixar exige conta.
              <Typography.Text type="secondary">Visitante sem conta</Typography.Text>
            ),
        },
        {
          title: 'O quê',
          dataIndex: 'kind',
          width: 110,
          render: (kind: ExamFileKind) => examFileLabel[kind],
        },
        {
          title: 'Ação',
          dataIndex: 'action',
          width: 130,
          render: (acao: UsoDaProva['action']) =>
            acao === 'download' ? (
              <Tag color="blue">Baixou</Tag>
            ) : (
              <Tag>Pediu impressão</Tag>
            ),
        },
        {
          title: 'Quando',
          dataIndex: 'createdAt',
          width: 150,
          render: (iso: string) => new Date(iso).toLocaleString('pt-BR'),
        },
      ]}
    />
  );
}
